import React, { useState } from 'react'

import {
    Button,
    CircularProgress,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { useTimezone } from '../../contexts/timezone'
import { useRecordsPersonNotes } from '../../hooks/useRecords'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    composer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },
    field: {
        width: '100%',
    },
    note: {
        padding: theme.spacing(1.5, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
        textAlign: 'left',
    },
    meta: {
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
        marginBottom: theme.spacing(0.5),
    },
    body: {
        whiteSpace: 'pre-wrap',
    },
    empty: {
        color: theme.palette.text.secondary,
        padding: theme.spacing(3, 0),
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
}))

interface PersonNotesProps {
    userId?: number | null
    leadId?: number | null
}

export const PersonNotes = ({
    userId = null,
    leadId = null,
}: PersonNotesProps) => {
    const classes = useStyles()
    const { formatDate } = useTimezone()
    const [body, setBody] = useState('')
    const { notes, isLoading, addNote, isSaving } = useRecordsPersonNotes(
        userId,
        leadId,
    )

    const submit = async () => {
        const text = body.trim()
        if (!text || isSaving) return
        try {
            await addNote(text)
            setBody('')
        } catch {
            return
        }
    }

    return (
        <div className={classes.root}>
            <div className={classes.composer}>
                <TextField
                    className={classes.field}
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder="Add a note to this file"
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    disabled={isSaving}
                />
                <Button
                    color="primary"
                    variant="contained"
                    disableElevation
                    disabled={!body.trim() || isSaving}
                    onClick={submit}
                >
                    Save note
                </Button>
            </div>
            {isLoading ? (
                <div className={classes.loading}>
                    <CircularProgress size={24} />
                </div>
            ) : notes.length === 0 ? (
                <Typography className={classes.empty}>
                    No notes on this file yet.
                </Typography>
            ) : (
                notes.map((note) => (
                    <div key={note.id} className={classes.note}>
                        <Typography className={classes.meta}>
                            {[
                                note.created_by_name || 'Staff',
                                note.created_date
                                    ? formatDate(note.created_date, 'date-only')
                                    : '',
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </Typography>
                        <Typography className={classes.body}>
                            {note.body}
                        </Typography>
                    </div>
                ))
            )}
        </div>
    )
}
